import React from 'react'
import { Box, Stack } from '@mui/material'
import toast from 'react-hot-toast'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { TagIcon } from '@heroicons/react/24/outline'
import { useSidebar } from '../../../shared/components/Sidebar'
import { SidebarSection } from '../../../shared/types/navigation'
import { DashboardInfoBox, DashboardBoxStatus } from '../../../interaxDesignSystemComponents'
import { ThisWeekSessions, ActiveSprintOverview } from '../../../components/dashboard'
import { useFacilitatorDashboard } from '../../../apiLayer'
import { PageHeader } from '../../../shared/components/PageHeader'
import { PageWrapper } from '../../../shared/components'
import { getActionBoxIcon, getActionBoxClickHandler, getInfoBoxIcon, FACILITATOR_ACTION_BOXES } from './Dashboard.utils'
import { buildFacilitatorDashboardData } from './Dashboard.helpers'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { useCanAccessGenerateReports } from '../../../shared/hooks/useCanAccessGenerateReports'
import { DashboardActionBoxType } from '../../../apiLayer/types/dashboard'
import { useBlockOnPendingWithoutData } from '../../organization/orgSwitch'

/**
 * Facilitator Dashboard page with new design system components
 */
export const DashboardPage: React.FC = () => {
  const { t } = useTranslation()
  const { setActiveSection } = useSidebar()
  const navigate = useNavigate()
  const { canAccessGenerateReports } = useCanAccessGenerateReports()

  // Fetch dashboard data from API layer
  const { data: dashboardData, isPending, error } = useFacilitatorDashboard()
  const hasDashboardData = Boolean(dashboardData?.success && dashboardData.data)
  const showBlockingLoader = useBlockOnPendingWithoutData(isPending, hasDashboardData)

  // Show non-blocking toast for API error (must be before any early return)
  React.useEffect(() => {
    if (error) {
      toast.error(`${t('facilitator.dashboard.error')}: ${error.message}`)
    } else if (dashboardData && dashboardData.success === false) {
      toast.error(`${t('facilitator.dashboard.error')}: ${dashboardData.error || t('common.somethingWentWrong')}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, dashboardData?.success])

  if (showBlockingLoader) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '16rem' }}>
        <Spinner size="large" color="sun" text={t('facilitator.dashboard.loading')} />
      </Box>
    )
  }

  // Build dashboard data from raw API data
  const rawData = dashboardData?.data
  const actionBoxes = canAccessGenerateReports
    ? FACILITATOR_ACTION_BOXES
    : FACILITATOR_ACTION_BOXES.filter((box) => box.type !== DashboardActionBoxType.GENERATE_REPORTS)

  const dashboardDataBuilt = rawData
    ? buildFacilitatorDashboardData(rawData, actionBoxes)
    : {
        actionBoxes,
        infoBoxes: [],
        thisWeekSessions: [],
        activeSprints: []
      }

  const computedInfoBoxes = dashboardDataBuilt.infoBoxes
  const computedThisWeekSessions = dashboardDataBuilt.thisWeekSessions
  const computedActiveSprints = dashboardDataBuilt.activeSprints
  const actionBoxCount = dashboardDataBuilt.actionBoxes.length
  const mediumActionBoxColumnCount = Math.min(Math.max(actionBoxCount, 1), 2)
  const largeActionBoxColumnCount = Math.min(Math.max(actionBoxCount, 1), 3)

  return (
    <PageWrapper>
      <PageHeader
        badgeText="FACILITATOR"
        badgeIcon={<TagIcon />}
        title={t('facilitator.dashboard.title')}
        subtitle={t('facilitator.dashboard.subtitle')}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: `repeat(${mediumActionBoxColumnCount}, minmax(0, 1fr))`,
            lg: `repeat(${largeActionBoxColumnCount}, minmax(0, 1fr))`
          },
          alignItems: 'stretch',
          paddingBottom: '32px'
        }}
      >
        {dashboardDataBuilt.actionBoxes.map((box) => {
          const IconComponent = getActionBoxIcon(box.type)
          return (
            <Box
              key={box.type}
              sx={{
                minWidth: 0,
                height: '100%',
                display: 'flex'
              }}
            >
              <DashboardInfoBox
                icon={<IconComponent style={{ width: 32, height: 32 }} />}
                heading={t(box.title)}
                description={t(box.description)}
                linkText={t(box.buttonText)}
                onLinkClick={getActionBoxClickHandler(box.type, setActiveSection, navigate)}
              />
            </Box>
          )
        })}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))'
          },
          alignItems: 'stretch',
          paddingBottom: '32px'
        }}
      >
        {computedInfoBoxes.map((infoBox, index) => {
          const IconComponent = getInfoBoxIcon(infoBox.title)

          let percentChange = 0
          if (infoBox.change) {
            const isIncrease = infoBox.change.includes('↑')
            const isDecrease = infoBox.change.includes('↓')
            const percentMatch = infoBox.change.match(/(\d+)%/)
            const percent = percentMatch ? parseInt(percentMatch[1]) : 0
            percentChange = isIncrease ? percent : isDecrease ? -percent : 0
          }

          const statusText = infoBox.status ? t(infoBox.status, infoBox.changeData) : ''

          return (
            <Box
              key={`${infoBox.title}-${index}`}
              sx={{
                minWidth: 0,
                height: '100%',
                display: 'flex'
              }}
            >
              <DashboardBoxStatus
                title={infoBox.title}
                activeCount={typeof infoBox.value === 'number' ? infoBox.value : parseInt(infoBox.value.toString())}
                percentChange={percentChange}
                changeText={statusText}
                icon={<IconComponent style={{ width: 24, height: 24 }} />}
              />
            </Box>
          )
        })}
      </Box>

      <Stack
        spacing={3}
        direction={{ xs: 'column', sm: 'column', lg: 'row' }}
        sx={{
          alignItems: 'stretch',
          '& > *': {
            flex: 1,
            minWidth: 0,
            display: 'flex'
          }
        }}
      >
        <Box sx={{ flexDirection: 'column', width: '100%' }}>
          <ThisWeekSessions
            sessions={computedThisWeekSessions}
            onViewAll={() => {
              setActiveSection(SidebarSection.SESSIONS)
              navigate('/facilitator/sessions')
            }}
            onUpcomingSessions={() => {
              setActiveSection(SidebarSection.SESSIONS)
              navigate('/facilitator/sessions')
            }}
          />
        </Box>

        <Box sx={{ flexDirection: 'column', width: '100%' }}>
          <ActiveSprintOverview
            sprints={computedActiveSprints}
            onViewAll={() => {
              setActiveSection(SidebarSection.TRAINING_SPRINT_MANAGEMENT)
              navigate('/facilitator/training-sprints')
            }}
            onViewDetails={(index) => {
              const sprint = computedActiveSprints?.[index]
              if (sprint && sprint.id) {
                navigate(`/facilitator/sprint/${sprint.id}`)
              }
            }}
          />
        </Box>
      </Stack>
    </PageWrapper>
  )
}
